import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Divider,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Email as EmailIcon,
  Work as WorkIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircle as CheckIcon,
  CalendarToday as CalendarIcon,
  BeachAccess as LeaveIcon,
  Timeline as TimelineIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Assignment as ContractIcon,
  Badge as BadgeIcon
} from '@mui/icons-material';
import apiService from '../services/apiService';
import { leaveBalances, documentService } from '../services/api';
import api from '../services/api';
import CareerEventsTimeline from './CareerEvents/CareerEventsTimeline';
import { useToast } from './Common/ToastProvider';

const UserProfile = () => {
  const toast = useToast();
  const [profileData, setProfileData] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [visibleFields, setVisibleFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [careerEvents, setCareerEvents] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [contractInfo, setContractInfo] = useState(null);
  const [loadingContract, setLoadingContract] = useState(false);

  // Helper to check if a field is visible
  const isVisible = useCallback((fieldKey) => visibleFields.includes(fieldKey), [visibleFields]);

  const fetchVisibleFields = useCallback(async () => {
    try {
      const fields = await api.get('/profile-settings/my-visible-fields');
      setVisibleFields(fields || []);
    } catch (err) {
      console.warn('Could not fetch visible fields, showing all:', err);
      // If API fails, show all fields as fallback
      setVisibleFields(['fullName', 'email', 'personalIdNumber', 'position', 'department',
        'seniorityLevel', 'hireDate', 'employeeStatus', 'contractType', 'contractStartDate',
        'contractEndDate', 'annualLeaveDays', 'careerEventsTab', 'documentsTab', 'leaveBalanceSection']);
    }
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getUserProfile();
      setProfileData(response.data);
    } catch (err) {
      console.error('Failed to load user profile:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      setError('Failed to load user profile: ' + errorMessage);
      if (err.response?.status === 401) {
        setError('Authentication failed. Please try logging out and logging back in.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVisibleFields();
    fetchUserProfile();
    fetchLeaveBalance();
  }, [fetchVisibleFields, fetchUserProfile]);

  const fetchLeaveBalance = async () => {
    try {
      const data = await leaveBalances.getMine();
      const items = Array.isArray(data) ? data : (data?.items || []);
      // Find paid leave balance (code "AL") - same logic as LeaveBalanceWidget
      const paidLeave = items.find(balance =>
        (balance.code || balance.Code) === 'AL'
      );
      setLeaveBalance(paidLeave);
    } catch (err) {
      console.warn('Could not fetch leave balance:', err);
      // Don't show error for leave balance - it's optional info
    }
  };

  const fetchCareerEvents = useCallback(async () => {
    if (!isVisible('careerEventsTab')) return;
    try {
      setLoadingEvents(true);
      const data = await api.get('/CareerEvents/my');
      setCareerEvents(data || []);
    } catch (err) {
      console.warn('Could not fetch career events:', err);
    } finally {
      setLoadingEvents(false);
    }
  }, [isVisible]);

  const fetchDocuments = useCallback(async () => {
    if (!isVisible('documentsTab')) return;
    try {
      setLoadingDocs(true);
      const data = await api.get('/documents/my');
      setDocuments(data || []);
    } catch (err) {
      console.warn('Could not fetch documents:', err);
    } finally {
      setLoadingDocs(false);
    }
  }, [isVisible]);

  // Load career events and documents when tab changes or visibility changes
  useEffect(() => {
    // Career events tab: index 1 when visible
    const careerTabIdx = isVisible('careerEventsTab') ? 1 : -1;
    // Documents tab: index 2 if career tab visible, otherwise 1
    const docsTabIdx = isVisible('documentsTab') ? (isVisible('careerEventsTab') ? 2 : 1) : -1;

    if (activeTab === careerTabIdx && careerEvents.length === 0) {
      fetchCareerEvents();
    }
    if (activeTab === docsTabIdx && documents.length === 0) {
      fetchDocuments();
    }
  }, [activeTab, isVisible, fetchCareerEvents, fetchDocuments, careerEvents.length, documents.length]);

  // Fetch contract info for Contract section
  const fetchContractInfo = useCallback(async () => {
    try {
      setLoadingContract(true);
      const data = await api.get('/contracts/my');
      setContractInfo(data);
    } catch (err) {
      console.warn('Could not fetch contract info:', err);
    } finally {
      setLoadingContract(false);
    }
  }, []);

  // Load contract info when profile loads and contract fields are visible
  useEffect(() => {
    const hasContractFields = isVisible('contractType') || isVisible('contractStartDate') ||
      isVisible('contractEndDate') || isVisible('annualLeaveDays');
    if (hasContractFields && profileData?.employee && !contractInfo) {
      fetchContractInfo();
    }
  }, [isVisible, profileData, contractInfo, fetchContractInfo]);

  const handleViewDocument = async (docId) => {
    try {
      const result = await documentService.getDocumentUrl(docId);
      if (result?.sasUrl) {
        window.open(result.sasUrl, '_blank');
      }
    } catch (err) {
      console.error('Failed to get document URL:', err);
    }
  };

  const handleViewContract = async () => {
    try {
      const result = await api.get('/contracts/my/file');
      if (result?.sasUrl) {
        window.open(result.sasUrl, '_blank');
      }
    } catch (err) {
      console.error('Failed to get contract URL:', err);
      toast('Contract file not available', { variant: 'error' });
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading profile...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchUserProfile}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  // Calculate which tabs to show
  const showCareerEventsTab = isVisible('careerEventsTab') && profileData?.employee;
  const showDocumentsTab = isVisible('documentsTab');

  // Calculate tab indices dynamically based on which tabs are visible
  const getTabIndex = (tabName) => {
    if (tabName === 'overview') return 0;
    if (tabName === 'careerEvents') return showCareerEventsTab ? 1 : -1;
    if (tabName === 'documents') {
      if (showCareerEventsTab) return 2;
      if (showDocumentsTab) return 1;
      return -1;
    }
    return -1;
  };

  const isTabActive = (tabName) => activeTab === getTabIndex(tabName);

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          My Profile
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your account information and access permissions
        </Typography>
      </Box>

      {/* Tabs - only show if at least one extra tab is visible */}
      {(showCareerEventsTab || showDocumentsTab) && (
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="fullWidth"
          >
            <Tab icon={<PersonIcon />} label="Overview" iconPosition="start" />
            {showCareerEventsTab && <Tab icon={<TimelineIcon />} label="Career Events" iconPosition="start" />}
            {showDocumentsTab && <Tab icon={<DescriptionIcon />} label="Documents" iconPosition="start" />}
          </Tabs>
        </Paper>
      )}

      {/* Tab: Overview */}
      {isTabActive('overview') && (
      <Stack spacing={2}>
        {/* Header Card - Name, Position, Status */}
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.5rem', fontWeight: 600 }}>
              {getInitials(profileData.displayName)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>{profileData.displayName}</Typography>
                {profileData.summary.isSystemAdmin && (
                  <Chip icon={<AdminIcon />} label="Admin" color="error" size="small" />
                )}
                <Chip
                  label={profileData.isActive ? 'Active' : 'Inactive'}
                  color={profileData.isActive ? 'success' : 'default'}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">{profileData.email}</Typography>
              {profileData.employee?.position && (
                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                  {profileData.employee.position}
                  {profileData.employee.department && ` • ${profileData.employee.department}`}
                </Typography>
              )}
            </Box>
            {/* Access Roles inline */}
            {profileData.roles && profileData.roles.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {profileData.roles.filter(r => r.name !== 'SystemAdmin').map((role) => (
                  <Chip key={role.id} label={role.name} size="small" variant="outlined" />
                ))}
              </Box>
            )}
          </Box>
        </Paper>

        {/* Info Grid - 3 columns on large screens */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          {/* Employment Card */}
          {profileData.employee && (isVisible('seniorityLevel') || isVisible('hireDate') || isVisible('employeeStatus')) && (
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WorkIcon fontSize="small" /> Employment
              </Typography>
              <Stack spacing={1}>
                {isVisible('seniorityLevel') && profileData.employee.seniorityLevel && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Seniority</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profileData.employee.seniorityLevel}</Typography>
                  </Box>
                )}
                {isVisible('hireDate') && profileData.employee.hireDate && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Hire Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(profileData.employee.hireDate)}</Typography>
                  </Box>
                )}
                {isVisible('employeeStatus') && profileData.employee.status && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profileData.employee.status}</Typography>
                  </Box>
                )}
                {isVisible('personalIdNumber') && profileData.employee.personalIdNumber && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Personal ID</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profileData.employee.personalIdNumber}</Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {/* Contract Card */}
          {profileData.employee && (isVisible('contractType') || isVisible('contractStartDate') || isVisible('contractEndDate') || isVisible('annualLeaveDays')) && (
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ContractIcon fontSize="small" /> Contract
              </Typography>
              <Stack spacing={1}>
                {isVisible('contractType') && (contractInfo?.contractType || profileData.employee.contractType) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Type</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{contractInfo?.contractType || profileData.employee.contractType}</Typography>
                  </Box>
                )}
                {contractInfo?.contractNumber && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Number</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{contractInfo.contractNumber}</Typography>
                  </Box>
                )}
                {isVisible('contractStartDate') && (contractInfo?.startDate || profileData.employee.contractStartDate) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Start</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(contractInfo?.startDate || profileData.employee.contractStartDate)}</Typography>
                  </Box>
                )}
                {isVisible('contractEndDate') && profileData.employee.contractEndDate && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">End</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(profileData.employee.contractEndDate)}</Typography>
                  </Box>
                )}
                {isVisible('annualLeaveDays') && (contractInfo?.annualLeaveDays || profileData.employee.annualLeaveDays) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Annual Leave</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{contractInfo?.annualLeaveDays || profileData.employee.annualLeaveDays} days</Typography>
                  </Box>
                )}
              </Stack>
              {contractInfo?.hasFile && (
                <Button variant="text" size="small" startIcon={<ViewIcon />} onClick={handleViewContract} sx={{ mt: 1.5, p: 0 }}>
                  View Document
                </Button>
              )}
              {loadingContract && <CircularProgress size={14} sx={{ mt: 1 }} />}
            </Paper>
          )}

          {/* Leave Balance Card */}
          {isVisible('leaveBalanceSection') && leaveBalance && (
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LeaveIcon fontSize="small" /> Leave Balance ({leaveBalance.year})
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>{leaveBalance.availableNow}</Typography>
                <Typography variant="body2" color="text.secondary">days available</Typography>
              </Box>
              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Entitled</Typography>
                  <Typography variant="body2">{leaveBalance.entitledDays}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Used</Typography>
                  <Typography variant="body2">{leaveBalance.usedDays}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Carried Over</Typography>
                  <Typography variant="body2">{leaveBalance.carriedOverDays}</Typography>
                </Box>
                {leaveBalance.reservedDays > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="warning.main">Reserved</Typography>
                    <Typography variant="body2" color="warning.main">{leaveBalance.reservedDays}</Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}
        </Box>
      </Stack>
      )}

      {/* Tab: Career Events */}
      {isTabActive('careerEvents') && showCareerEventsTab && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <CareerEventsTimeline
            readOnly={true}
            events={careerEvents}
            loading={loadingEvents}
          />
        </Paper>
      )}

      {/* Tab: Documents */}
      {isTabActive('documents') && showDocumentsTab && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon />
            My Documents
          </Typography>
          {loadingDocs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : documents.length === 0 ? (
            <Alert severity="info">No documents to display.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Document Type</TableCell>
                    <TableCell>File Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell>Expiry</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <Chip label={doc.typeName} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{doc.fileName}</TableCell>
                      <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                      <TableCell>{formatDate(doc.uploadedAt)}</TableCell>
                      <TableCell>
                        {doc.expiryDate ? (
                          <Typography
                            variant="body2"
                            color={doc.isExpired ? 'error' : 'text.primary'}
                          >
                            {formatDate(doc.expiryDate)}
                            {doc.isExpired && ' (Expired)'}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Document">
                          <IconButton size="small" onClick={() => handleViewDocument(doc.id)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default UserProfile;
